import { ImgHTMLAttributes } from "react";

import { TransformationOptions, buildImageUrl } from "@shared/utils";

// - regular image attributes without width and height
// - transformation options
// - src required
type ImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "width" | "height"
> &
  Omit<TransformationOptions, "dpr"> & {
    src: string;
    maxDpr?: TransformationOptions["dpr"];
  };

/**
 * A component that renders an image with responsive sizing and DPR (Device Pixel Ratio) support
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} props.alt - Alternative text for the image
 * @param {string} props.src - Source URL of the image
 * @param {number} [props.w] - Desired width of the image
 * @param {number} [props.h] - Desired height of the image
 * @param {string} [props.rt] - Resizing type (e.g., to fit or fill the image)
 * @param {string} [props.ar] - Aspect Ratio (e.g., "16:9", "4:3". Combine it with `w` or `h` to get the best result)
 * @param {number} [props.maxDpr=1] - Maximum Device Pixel Ratio to support (creates srcSet variants)
 * @param {Object} props.rest - Additional props to pass to the img element
 *
 * @returns {JSX.Element} An img element with optional srcSet for different DPR support
 *
 * @example
 * ```jsx
 * <ResizedImage
 *   src="image.jpg"
 *   alt="My Image"
 *   w={800}
 *   h={600}
 *   maxDpr={2}
 * />
 * ```
 */
export default function ResizedImage({
  alt,
  src: originalSrc,
  w,
  h,
  rt,
  ar,
  maxDpr = 1,
  ...rest
}: ImageProps) {
  const src = buildImageUrl(originalSrc, { w, h, rt, ar });
  const srcSet =
    maxDpr > 1
      ? Array.from(
          { length: maxDpr },
          (_, i) => (i + 1) as TransformationOptions["dpr"],
        )
          .map(
            (dpr) =>
              `${buildImageUrl(originalSrc, { w, h, rt, dpr, ar })} ${dpr}x`,
          )
          .join(", ")
      : undefined;

  // eslint-disable-next-line @next/next/no-img-element
  return <img srcSet={srcSet} src={src} alt={alt} {...rest} />;
}
